'use client'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { desktopVw } from '../../styles/utils'
import { validateEmail } from '../../utils'
import { useCurrentUser, useAuth } from '@lastbrand/fe-common/universal-auth'

import useReferralPoints from '../../hooks/useReferralPoints'
import AuthWrapper from '../../components/Auth/AuthWrapper'
import FormFooter from '../../components/Auth/FormFooter'
import { Container, ErrorMessage, Input } from '../../components/FormElements'
import Success from '../../components/ReferAndEarn/Success'
import { BUILD_ENVIRONMENTS } from '@src/config/constants'
import { CONFIG_REPORTING_SERVICES } from '@src/config/env'
import appData from '@src/quince-site-common/src/helpers/appData'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent, trackEvent } from '@src/telemetry/track'
import RichTextComp from '../../../../quince-components/src/molecules/RichText'
import RichText from '../../../../quince-site-common/src/domains/RichText'
import { ReferralFormHeader, StyledButton } from '../../components/ReferAndEarn/ReferralStyle'
import { captureEvent } from '../../components/Sentry/utils'
import useBearerFetch from '../../hooks/useBearerFetch'
import useDetectDevices from '../../hooks/useDetectDevices'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import { useTranslation } from 'next-i18next'

const StyledReferAndEarn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledInput = styled(Input)`
  padding: 16px 16px 0px;
  height: 50px;
  width: 314px;

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    padding: ${desktopVw(16)} ${desktopVw(16)} 0;
    width: ${desktopVw(350)};
  }
`

const StyledRefFooter = styled(FormFooter)`
  margin: 0 30px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: center;

  p {
    display: block;
    text-align: center;
    white-space: pre-wrap;
    font-size: 13px;
    line-height: 19px;
    font-family: ${props => props.theme.fonts.grosa};

    a {
      font-size: 13px;
      line-height: 19px;
      margin: unset;
      font-weight: unset;
      text-transform: unset;
      letter-spacing: unset;
    }
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin: 0 0 ${desktopVw(92)};

    p {
      font-size: ${desktopVw(13)};
      line-height: ${desktopVw(19)};
      max-width: ${desktopVw(600)};

      a {
        font-size: ${desktopVw(13)};
        line-height: ${desktopVw(19)};
      }
    }
  }
`

const ReferAndEarn = ({ data }) => {
  // @i18n-ignore
  const { t } = useTranslation('common')
  const router = useRouter()
  const page = data.authenticationPage
  const [stage, setStage] = useState(0)
  const [formData, setFormData] = useState({})
  const [errorMsg, setErrorMsg] = useState('')

  const { user } = useCurrentUser()

  const { isLoggedIn } = useAuth()

  const email = user?.email || ''

  useEffect(() => {
    if (email) {
      setFormData({
        sourceEmail: email,
        referralLink: '',
      })
    }
  }, [email])

  const handleChange = e => {
    setErrorMsg('')

    const { name } = e.target
    const { value } = e.target
    setFormData({
      ...formData,
      [name]: value,
      referralLink: '',
    })
  }

  const isReferralApiSupported = appData.environmentType !== BUILD_ENVIRONMENTS.STAGE
  const { fetchData, response, error, loading } = useBearerFetch()
  // @i18n-ignore
  const url = `${CONFIG_REPORTING_SERVICES}/v2-referralEmails/sendEmailToReferred`

  const handleSubmit = e => {
    e.preventDefault()

    const { sourceEmail, targetEmail } = formData

    if (!validateEmail(sourceEmail)) {
      setErrorMsg(t('please_enter_valid_email_address'))
      return
    }

    if (!targetEmail) {
      setErrorMsg(t('please_enter_at_least_one_email'))
      return
    }

    if (!isLoggedIn && !getIsNativeViewClient()) {
      const url = new URL(window.location.href)
      url.searchParams.set('targetEmail', targetEmail)
      router.push(`/log-in?redirecturi=${encodeURIComponent(url.href)}`)
    }

    const targetEmailArr = targetEmail.split(',')

    for (const e of targetEmailArr) {
      if (!validateEmail(e.trim())) {
        setErrorMsg(t('email_validation_error', { email: e }))
        return
      }
    }

    if (!loading) {
      const body = { ...formData, targetEmail: targetEmailArr }

      const options = {
        // @i18n-ignore
        method: 'POST',
        headers: {
          // @i18n-ignore
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }

      isReferralApiSupported && fetchData(url, options)
      trackEvent(TELEMETRY_EVENT_NAMES.SEND_REFERRAL_EMAIL_INVITE, {
        email: sourceEmail,
        targetEmail,
      })
      trackClickEvent(TELEMETRY_EVENT_NAMES.FORM_SUBMIT, {
        trackGA: true,
        trackInternal: true,
        // @i18n-ignore
        category: 'referral',
        action: 'send_invite',
      })
    }
  }

  const [referralCode, setReferralCode] = useState('')

  const { fetchReferrals, successfulReferrals, invitedReferrals } = useReferralPoints(formData.sourceEmail)
  const redirectUri = isBrowser() ? new URL(window.location.href) : undefined
  const targetEmail = redirectUri?.searchParams.get('targetEmail')

  useEffect(() => {
    if (targetEmail) {
      setFormData(data => ({
        ...data,
        targetEmail,
      }))
    }
  }, [targetEmail])

  useEffect(() => {
    if (!response && !error) {
      return
    }

    if (response?.code) {
      setReferralCode(response.code)
      // Reset field
      setFormData(formData => ({
        ...formData,
        targetEmail: '',
      }))
      setStage(1)
      fetchReferrals()
    } else if (response?.error) {
      setErrorMsg(t('an_error_occurred_while_r_qmoet'))
      captureEvent(
        `Error in sending referral email. Server responded with error: ${response.error} and message: ${response.message}. Sent by : ${formData.sourceEmail} & Sent to: ${formData.targetEmail}.`,
      )
    } else if (error) {
      const errorMsg = error?.responseBody?.message
      setErrorMsg(errorMsg || t('an_error_occurred_while_r_qmoet'))
      captureEvent(
        `Error in sending referral email. There was an error : ${error} in sending referrals. Sent by : ${formData.sourceEmail} & Sent to: ${formData.targetEmail}`,
      )
    } else if (response && !response.code) {
      captureEvent(
        `Error in sending referral email. Empty response code. Sent by : ${formData.sourceEmail} & Sent to: ${formData.targetEmail}`,
      )
    }
    // TODO: Need to properly fix the dependencies array as current implementation causes infinite loop if all dependencies are added
  }, [response, error, fetchReferrals, formData.sourceEmail, formData.targetEmail, t])

  const { devices } = useDetectDevices()
  const richTextsData = page?.richText?.map(item => RichText.makeRichTextProps(RichText.fromContentful(item)))
  return (
    <>
      <StyledReferAndEarn>
        <AuthWrapper
          image={devices.mobile ? page.imageMobile : page.image}
          referImage
          imageText={<RichTextComp {...richTextsData[0]} />}
          stage={stage}
          referLayout
        >
          {stage === 0 && (
            <form onSubmit={handleSubmit}>
              <ReferralFormHeader marginTop={isLoggedIn}>
                <div className="mobile-single-line">
                  <RichTextComp {...richTextsData[1]} />
                </div>
              </ReferralFormHeader>
              <Container>
                <StyledInput
                  // @i18n-ignore
                  type="email"
                  placeholder={t('your_email')}
                  name="sourceEmail"
                  onChange={handleChange}
                  defaultValue={email}
                  value={formData?.sourceEmail || email}
                  readOnly={isLoggedIn}
                />
              </Container>
              <Container>
                <StyledInput
                  // @i18n-ignore
                  type="text"
                  name="targetEmail"
                  // @i18n-ignore
                  placeholder={t('your_friend_s_email_separ_l7ftt')}
                  onChange={handleChange}
                  value={formData.targetEmail}
                />
              </Container>
              <Container>
                <StyledButton
                  black
                  // @i18n-ignore
                  type="submit"
                  loading={loading}
                >
                  {page.buttonText}
                </StyledButton>
              </Container>
              {errorMsg && (
                <Container>
                  <ErrorMessage alignCenter>{errorMsg}</ErrorMessage>
                </Container>
              )}
            </form>
          )}
          {stage === 1 && (
            <Success
              handleSubmit={handleSubmit}
              targetEmail={formData.targetEmail}
              referralCode={referralCode}
              handleChange={handleChange}
              successfulReferrals={successfulReferrals}
              invitedReferrals={invitedReferrals}
              submitButtonText={page.buttonText}
              loading={loading}
            />
          )}
        </AuthWrapper>
        {stage === 0 && (
          <StyledRefFooter>
            <RichTextComp {...richTextsData[2]} />
          </StyledRefFooter>
        )}
      </StyledReferAndEarn>
    </>
  )
}

export default ReferAndEarn
